import React, { useEffect, useMemo, useRef, useState } from 'react';
import { CSSTransition } from 'react-transition-group';
import produce from 'immer';
import { DateTime } from 'luxon';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { ReportsModel } from './ReportsModel';
import { Button, Modal, ModalHeader, ModalBody, ModalFooter  } from '@cfa/react-components';
import { assertUnreachable } from 'services/Utils';
import { ReportsFieldType } from './enums/ReportsFieldType';

interface IReportsExportColumnModalProps {
    model: ReportsModel,
    show: boolean,
    onHide: () => void,
}

interface IState {
    escapeVals: boolean,
    fieldId: string,
    separatorComma: boolean,
    separatorNewline: boolean,
    showCopied?: boolean,
}

export function ReportsExportColumnModal({ model, show, onHide }: IReportsExportColumnModalProps) {
    const [{
        escapeVals,
        fieldId,
        separatorComma,
        separatorNewline,
        showCopied 
    }, setState] = useState<IState>(() => {
        return {
            // Default to first column in the output
            fieldId: model.outFields?.[0]?.id || '',
            separatorComma: false,
            separatorNewline: true,
            escapeVals: false,
        }
    });

    const textareaValue = useMemo<string>(() => {
        const field = model.allFieldsById[fieldId];
        if (!field) return '';

        const vals: string[] = new Array(model.outRows.length);
        const propName = field.propName;

        for (let i = 0; i < model.outRows.length; i++) {
            switch (field.type) {
                case ReportsFieldType.Date:
                    const date = model.outRows[i][propName] as DateTime;
                    if (date.isValid) {
                        vals[i] = date.toISO();
                    } else {
                        vals[i] = '<invalid_date>'
                    }
                    break;
                case ReportsFieldType.String:
                case ReportsFieldType.Number:
                    vals[i] = model.outRows[i][propName] + '';
                    break;
                default:
                    assertUnreachable(field.type);
            }
        }

        // If we're quoting values, then we need to escape any double quotes.
        const doubleQuotesRx = /"/g;
        if (escapeVals) {
            for (let i = 0; i < vals.length; i++) {
                vals[i] = `"` + vals[i].replace(doubleQuotesRx, `""`) + `"`;
            }
        }

        const comma = separatorComma ? ',' : '';
        const newLine = separatorNewline ? '\n' : ' ';

        return vals.join(`${comma}${newLine}`)
    }, [fieldId, separatorComma, separatorNewline, escapeVals]);

    function onChangeFieldId(e: React.ChangeEvent<HTMLSelectElement>) {
        const newFieldId = e.target.value;
        setState(produce(draft => {
            draft.fieldId = newFieldId;
        }))
    }

    function onChangeComma(e: React.ChangeEvent<HTMLInputElement>) {
        const newSeparatorComma = e.target.checked;
        setState(produce(draft => {
            draft.separatorComma = newSeparatorComma;
        }))
    }

    function onChangeNewline(e: React.ChangeEvent<HTMLInputElement>) {
        const newSeparatorNewline = e.target.checked;
        setState(produce(draft => {
            draft.separatorNewline = newSeparatorNewline;
        }))
    }

    function onChangeEscape(e: React.ChangeEvent<HTMLInputElement>) {
        const newEscapeVals = e.target.checked;
        setState(produce(draft => {
            draft.escapeVals = newEscapeVals;
        }))
    }

    const copiedTimeoutRef = useRef<any>();
    useEffect(() => {
        return () => {
            clearTimeout(copiedTimeoutRef.current);
        }
    }, []);

    function onCopyToClipboard() {
        navigator.clipboard.writeText(textareaValue).then(() => {
            // Show an animated little message
            setState(produce(draft => {
                draft.showCopied = true;
            }));

            clearTimeout(copiedTimeoutRef.current);
            copiedTimeoutRef.current = setTimeout(() => {
                setState(produce(draft => {
                    draft.showCopied = false;
                }));
            }, 2500);
        }, (err) => {
            console.error('Error copying to clipboard: ', err);
        });
    }

    return (
        <Modal show={show} onClose={onHide}>
            <ModalHeader>
                Export Column
            </ModalHeader>
            <ModalBody>
                <div className="row" style={{'marginTop': '0px'}}>
                    <div className="col">
                        This will export a single column from the results.
                        This is useful if you want to copy and paste the results into an update's filter,
                        like for locations or serial numbers.
                    </div>
                </div>
                <div className="row">
                    <div className="col">
                        <div className="formItem">
                            <div className="formItem_label">
                                Column
                            </div>
                            <select
                                className="formItem_select"
                                value={fieldId}
                                onChange={onChangeFieldId}
                            >
                                <option key={""}> </option>
                                {model.outFields.map(f => (
                                    <option key={f.id} value={f.id}>
                                        {f.label}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>
                </div>
                <div className="row">
                    <div className="col">
                        <div className="formItem">
                            <div className="formItem_label">
                                Options
                            </div>
                            <div style={{textAlign: "left"}}>
                                <label>
                                    <input type="checkbox" checked={!!separatorComma} onChange={onChangeComma} />
                                    Separate with comma
                                </label>
                            </div>
                            <div style={{textAlign: "left"}}>
                                <label>
                                    <input type="checkbox" checked={!!separatorNewline} onChange={onChangeNewline} />
                                    Separate with newline
                                </label>
                            </div>
                            <div style={{textAlign: "left"}}>
                                <label>
                                    <input type="checkbox" checked={!!escapeVals} onChange={onChangeEscape} />
                                    Quote values
                                </label>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="row">
                    <div className="col">
                        <div className="formItem">
                            <div className="formItem_label">
                                Values
                            </div>
                            <textarea
                                value={textareaValue}
                                readOnly={true}
                                className="exportColumnModal_textarea"
                                rows={8}
                            />
                        </div>
                    </div>
                </div>
                <div className="row">
                    <div className="col">
                        <div className="exportColumnModal_copyButtonWrap">
                            <Button variant="outlined" color="secondary" size="sm" onClick={onCopyToClipboard}>
                                <FontAwesomeIcon icon="copy" /> Copy to clipboard
                            </Button>
                            <CSSTransition
                                in={showCopied}
                                timeout={150}
                                classNames="exportColumnModal_copied"
                                unmountOnExit
                            >
                                <span className="exportColumnModal_copied">Copied!</span>
                            </CSSTransition>
                        </div>
                    </div>
                </div>
            </ModalBody>
            <ModalFooter>
                <Button onClick={onHide}>
                    Close
                </Button>
            </ModalFooter>
        </Modal>
    );
}